/**
 * HostMessagesPage Component
 *
 * Main page for host messaging with guests.
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HostLayout } from '../components';
import { useHostMessages, type MessageThread as MessageThreadType } from './hooks';
import { ThreadItem, MessageThread } from './components';

type FilterType = 'all' | 'unread' | 'active' | 'pending' | 'archived';

const FILTER_OPTIONS: { id: FilterType; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending' },
  { id: 'archived', label: 'Archived' },
];

export const HostMessagesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFilter = (searchParams.get('filter') as FilterType) || 'all';
  const selectedThreadId = searchParams.get('thread');

  const [searchQuery, setSearchQuery] = useState('');

  const { threads, totalUnread, isLoading, error } = useHostMessages(activeFilter);

  // Find selected thread
  const selectedThread = threads.find((t) => t.id === selectedThreadId);

  // Filter threads by search
  const filteredThreads = searchQuery
    ? threads.filter(
        (t) =>
          t.guestName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.listingTitle.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : threads;

  const handleFilterChange = (filter: FilterType) => {
    const params = new URLSearchParams(searchParams);
    params.set('filter', filter);
    if (selectedThreadId) params.set('thread', selectedThreadId);
    setSearchParams(params);
  };

  const handleThreadSelect = (thread: MessageThreadType) => {
    const params = new URLSearchParams(searchParams);
    params.set('thread', thread.id);
    setSearchParams(params);
  };

  if (error) {
    return (
      <HostLayout title="Messages">
        <div className="bg-red-50 text-red-600 p-4 rounded-xl">
          Error loading messages: {error}
        </div>
      </HostLayout>
    );
  }

  return (
    <HostLayout title="Messages">
    <div className="h-[calc(100vh-180px)] flex -mx-4 lg:-mx-8 -mb-24 lg:-mb-8">
      {/* Thread List Sidebar - full width on mobile when no thread selected, hidden when thread open */}
      <aside className={`${selectedThread ? 'hidden md:flex' : 'flex'} w-full md:w-80 lg:w-96 border-r bg-white flex-col`}>
        {/* Header */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900">Messages</h1>
            {totalUnread > 0 && (
              <span className="px-2 py-0.5 bg-primary-500 text-white text-sm rounded-full">
                {totalUnread} new
              </span>
            )}
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <SearchIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-1 overflow-x-auto pb-1">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => handleFilterChange(option.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  activeFilter === option.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {option.label}
                {option.id === 'unread' && totalUnread > 0 && (
                  <span className="ml-1">({totalUnread})</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Thread List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse flex gap-3">
                  <div className="w-12 h-12 bg-gray-200 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                    <div className="h-3 bg-gray-200 rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredThreads.length === 0 ? (
            <div className="p-8 text-center">
              <MessageIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No messages</h3>
              <p className="text-sm text-gray-500">
                {searchQuery
                  ? 'No messages match your search'
                  : activeFilter === 'unread'
                  ? "You're all caught up!"
                  : "You don't have any messages yet"}
              </p>
            </div>
          ) : (
            filteredThreads.map((thread) => (
              <ThreadItem
                key={thread.id}
                thread={thread}
                isSelected={selectedThreadId === thread.id}
                onClick={() => handleThreadSelect(thread)}
              />
            ))
          )}
        </div>
      </aside>

      {/* Message Thread - full width on mobile when thread is selected */}
      <main className={`${selectedThread ? 'flex' : 'hidden md:flex'} flex-1 bg-gray-50`}>
        {selectedThread ? (
          <div className="flex-1 flex flex-col">
            {/* Back button on mobile */}
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.delete('thread');
                setSearchParams(params);
              }}
              className="md:hidden flex items-center gap-2 px-4 py-3 border-b bg-white text-sm text-gray-700"
            >
              <BackIcon className="w-4 h-4" />
              Back to messages
            </button>
            <div className="flex-1 flex">
              <MessageThread thread={selectedThread} />
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
            <MessageIcon className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">Select a conversation</h3>
            <p className="text-sm">Choose a message thread from the sidebar to view</p>
          </div>
        )}
      </main>
    </div>
    </HostLayout>
  );
};

// Icons
const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const MessageIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    />
  </svg>
);

const BackIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

export default HostMessagesPage;
